import { FC } from "react";

import { Box, Stack, Theme, useTheme } from "@mui/material";
import { Nullable } from "@simplyk/common";

import { VerifiedOrganizationBadge } from "@/canvas/components/VerifiedOrganizationBadge/VerifiedOrganizationBadge";
import {
  ORGANIZATION_PAGE_LINK_HOVER_SELECTOR,
  useOrganizationPageLinkProps,
} from "@/canvas/components/OrganizationLogoAndName/helpers/useOrganizationPageLinkProps";
import { mergeSx } from "@/common/helpers/mergeSx";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FORM_ELEMENT_IDS } from "@/features/LiveFormEditor/Steps/helpers/formStepToElementIdMap";
import { FormBlock, FormBlockProps } from "@/forms-design-system/FormBlock/FormBlock";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { ExternalLink } from "@/icons/outlined";

type OrganizationLogoAndNameProps = Omit<FormBlockProps, "ref" | "variant" | "data-test"> & {
  name: string;
  logoSrc?: string;
  withFallbackImage?: boolean;
  publishedOrgPageUrl?: Nullable<string>;
  blockVariant?: "card" | "inline";
  isVerified?: boolean;
};

export const OrganizationLogoAndName: FC<OrganizationLogoAndNameProps> = ({
  name,
  logoSrc,
  sx,
  withFallbackImage = false,
  publishedOrgPageUrl,
  blockVariant = "card",
  isVerified = false,
  ...rest
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();
  const organizationPageLinkProps = useOrganizationPageLinkProps(publishedOrgPageUrl);

  const { fallbackImageBackgroundColor } = useDynamicThemeAwareVariables(({ mode }) => ({
    fallbackImageBackgroundColor:
      mode === "light" ? theme.palette.forms.surface.quiet : theme.palette.forms.surface.supershy,
  }));

  const logoSize = isSmallScreen ? 32 : 40;

  const styles = mergeSx(
    {
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      minHeight: 36,
      gap: { xs: 1, md: 1.5 },
      textDecoration: "none",
      padding: { xs: theme.spacing(0.75, 1), md: theme.spacing(1, 2) },
    },
    sx
  );

  const logoStyles = {
    borderRadius: theme.radius(0.5),
    width: logoSize,
    height: logoSize,
    objectFit: "contain",
  };

  const nameTypography = (
    <FormTypography
      variant={isSmallScreen ? "subtitle2" : "h6"}
      vibe="intense"
      sx={(theme: Theme) => ({
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        [ORGANIZATION_PAGE_LINK_HOVER_SELECTOR]: {
          textDecoration: isSmallScreen ? "none" : "underline",
          textDecorationColor: theme.palette.forms.text.intense,
        },
      })}
    >
      {name}
    </FormTypography>
  );

  return (
    <Stack alignItems="flex-start">
      <FormBlock
        id={FORM_ELEMENT_IDS["logo"]}
        variant={blockVariant}
        data-test="form-v2-logo-chip"
        {...rest}
        {...organizationPageLinkProps}
        sx={styles}
      >
        {logoSrc ? (
          <Image src={logoSrc} alt={name} width={80} height={80} sx={logoStyles} nextImage />
        ) : withFallbackImage ? (
          <Box
            width={logoSize}
            height={logoSize}
            sx={mergeSx(logoStyles, { backgroundColor: fallbackImageBackgroundColor })}
          />
        ) : null}
        <Stack minWidth={0}>
          {publishedOrgPageUrl ? (
            <Stack direction="row" alignItems="center" gap={{ xs: 1, md: 0.5 }} maxWidth="100%">
              {nameTypography}
              <FormIcon
                size="small"
                sx={(theme: Theme) => ({
                  flexShrink: 0,
                  color: isSmallScreen ? theme.palette.forms.text.supershy : theme.palette.forms.text.quiet,
                })}
              >
                <ExternalLink />
              </FormIcon>
            </Stack>
          ) : (
            nameTypography
          )}
          {isVerified && <VerifiedOrganizationBadge />}
        </Stack>
      </FormBlock>
    </Stack>
  );
};
